import React from 'react';
import {
  Box,
  Button,
  DialogActions as MuiDialogActions,
  useMediaQuery,
  SxProps,
  Theme,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DialogActionsProps } from '../types';
import { useTranslation } from 'react-i18next';

export const dialogActionsSx = (theme: Theme): SxProps<Theme> => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.action.hover,
  padding: theme.spacing(2),
  flexDirection: { xs: 'column', sm: 'row' }, // Responsive layout for different screen sizes
  alignItems: 'stretch',
  gap: theme.spacing(4),
});

export const dialogActionContainerSx = (theme: Theme): SxProps<Theme> => ({
  display: 'flex',
  justifyContent: 'flex-end',
  flexDirection: 'row',
  width: '100%',
  gap: theme.spacing(1),
});

export const dialogActionButtonSx = (
  theme: Theme,
  isMobile: boolean,
): SxProps<Theme> => ({
  width: {
    xs: '100%',
    sm: 'auto',
  },
  height: isMobile ? 48 : 'auto',
  fontSize: isMobile ? theme.typography.button.fontSize : 'inherit',
});

// DialogActions: A customizable component for dialog action buttons
export const DialogActions = React.memo(
  ({
    showBackButton = true,
    showNextButton = true,
    nextButtonText,
    nextButtonDisabled = false,
    backButtonText,
    backButtonDisabled = false,
    customNextButton = null,
    onBack,
    onNext,
    activeStep = 0,
    nextButtonColor = 'primary',
  }: DialogActionsProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Default translations for button texts
    const defaultNextButtonText = t('blox.actions.next', 'Next');
    const defaultBackButtonText = t('blox.actions.back', 'Back');

    return (
      <MuiDialogActions sx={dialogActionsSx(theme)}>
        <Box
          sx={dialogActionContainerSx(theme)}
          data-testid="dialog-actions-container"
        >
          {/* Back Button */}
          {showBackButton && (
            <Button
              data-testid="dialog-back-button"
              disabled={activeStep === 0 || backButtonDisabled}
              onClick={onBack}
              sx={dialogActionButtonSx(theme, isMobile)}
            >
              {backButtonText || defaultBackButtonText}
            </Button>
          )}
          {/* Next Button: either custom or default */}
          {customNextButton ||
            (showNextButton && (
              <Button
                data-testid="dialog-next-button"
                variant="contained"
                color={nextButtonColor}
                onClick={onNext}
                disabled={nextButtonDisabled}
                sx={dialogActionButtonSx(theme, isMobile)}
                disableElevation
              >
                {nextButtonText || defaultNextButtonText}
              </Button>
            ))}
        </Box>
      </MuiDialogActions>
    );
  },
);
